import { api } from '@/api/client';
import { apiErrorMessage, httpStatus } from '@/lib/apiError';
import { db, type MutationEntry } from './db';
import { getAttachment } from './attachments';
import type { ApiResponse } from '@demoops/shared-types';
import { replayEntry, type ReplayCoreKind, type ReplayDeps, type ReplayHttp } from './replayCore';

// Payload shapes for the kinds replayCore owns — re-exported so feature code
// keeps importing them from here.
export type {
  OfflineFormSubmission, OfflineDailyLogFull, OfflineIncidentFull,
} from './replayCore';

export type ReplayOutcome =
  | { status: 'completed' }
  | { status: 'conflict'; conflicts: unknown[] };

/** Thrown when a replay fails permanently (4xx) — retrying won't help. */
export class TerminalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TerminalError';
  }
}

/**
 * 4xx (except 408/429) are terminal; network/5xx are retryable (rethrow raw).
 *
 * PROJECT_ACCESS_POLICY rule 9 / P10: a mutation queued offline can be
 * replayed against a project the user has since LOST access to, and the server
 * rejects it. That used to surface in the Sync Center as the bare string
 * "403", which tells the person nothing and looks like a bug. Access denials
 * now carry a plain reason so the queue explains itself; other 4xx keep the
 * status so existing behaviour is unchanged.
 */
function classifyAndThrow(err: unknown): never {
  const s = httpStatus(err);
  if (s === 403) {
    throw new TerminalError(
      apiErrorMessage(err) ?? 'You no longer have access to this project, so this change was not saved.',
    );
  }
  if (s === 404) {
    throw new TerminalError(
      apiErrorMessage(err) ?? 'That record is no longer available, so this change was not saved.',
    );
  }
  if (s && s >= 400 && s < 500 && s !== 408 && s !== 429) {
    throw new TerminalError(String(s));
  }
  throw err;
}

type Replay = (entry: MutationEntry) => Promise<ReplayOutcome>;

// ---- replayCore delegation (offline reliability §1) ----
// The daily-log, form-submission and incident kinds are replayed by
// offline/replayCore.ts — the same code the service worker runs with no tab
// open — through the app's axios client (CSRF cookie, correlation id, error
// envelope). This adapter is the whole difference between the two callers.
const apiHttp: ReplayHttp = {
  post: (url, body) => (body instanceof FormData
    ? api.post(url, body, { headers: { 'Content-Type': 'multipart/form-data' } })
    : api.post(url, body)),
};
const coreDeps: ReplayDeps = { http: apiHttp, db, now: () => Date.now() };

/** Registry entry for a replayCore kind: outcome → the registry's contract
 *  (`completed`, TerminalError, or the raw retryable error rethrown). */
function viaCore(kind: ReplayCoreKind): Replay {
  return async (entry) => {
    const result = await replayEntry({ ...entry, entityType: kind }, coreDeps);
    if (result.outcome === 'done') return { status: 'completed' };
    if (result.outcome === 'terminal') throw new TerminalError(result.message);
    throw result.error ?? new Error(result.message);
  };
}

// ---- Offline inspection-with-photos payload (blobs in the attachment store) ----
export interface OfflineInspectionFull {
  equipment_id: string;
  // offline_id is REQUIRED: the upsert POST is idempotent on
  // (equipment_id, offline_id), so a re-POST after a failed progress save
  // never creates a duplicate inspection.
  inspection: Record<string, unknown> & { offline_id: string };
  // Blobs always come from the attachment store here (no inline `blob?`).
  attachments: { field_name: string; filename: string; attachmentId: string }[];
}
interface InspectionFullProgress {
  inspectionId?: string;
  attachmentsDone?: number;
}

// ---- Offline purchase-request draft payloads (PO-FE-01, contract §1: only
// request-draft create/update may queue; every transition endpoint is
// online-only and must NEVER appear here) ----
export interface OfflinePurchaseRequestDraft {
  // offline_id is REQUIRED: the create POST is idempotent on offline_id, so a
  // replay returns the original request — never a duplicate (contract §1).
  request: Record<string, unknown> & { offline_id: string };
  // Blobs always come from the attachment store; document_type is the frozen
  // photo|quote|supporting vocabulary (contract §2).
  attachments: { document_type: string; filename: string; attachmentId: string }[];
}
interface PurchaseRequestDraftProgress {
  requestId?: string;
  attachmentsDone?: number;
}

/** Safety observation + staged photos (Phase 2 slice 1) — mirrors incidents. */
export interface OfflineSafetyObservationFull {
  observation: Record<string, unknown> & { offline_id: string };
  attachments: Array<{ field_name: string; filename: string; attachmentId: string }>;
}

interface SafetyObservationFullProgress {
  observationId?: string;
  attachmentsDone?: number;
}

/** Stop-work initiation — no photos; one idempotent create. */
export interface OfflineStopWorkCreate {
  event: Record<string, unknown> & { offline_id: string };
}

/**
 * Maps an outbox entry's `entityType` (kind) to its replay call.
 * Payloads carry their own `offline_id` (== idempotencyKey); the server dedupes
 * replays (verified: same offline_id never creates a duplicate).
 */
export const replayRegistry: Record<string, Replay> = {
  // 'assignment.create' and 'crew.assign' were removed on 2026-08-10 with the
  // day schedule. A queued entry of either kind now lands as `failed` with
  // "No replay handler for …" in the Sync Center (SyncContext), which is what
  // we want: visible, not a silent 404 retry loop against a route that is gone.

  // Workforce roster bulk assign. Server dedupes per employee on
  // (created_by, offline_id + ':' + employee_id); warnings are non-blocking,
  // so a replayed success is always 'completed'.
  'workforce.assign': async (entry) => {
    const { project_id, ...body } = entry.payload as { project_id: string } & Record<string, unknown>;
    try {
      await api.post(`/api/v1/projects/${project_id}/workforce`, body);
      return { status: 'completed' };
    } catch (e) { return classifyAndThrow(e); }
  },

  'equipmentReading.create': async (entry) => {
    const { equipment_id, ...body } = entry.payload as { equipment_id: string } & Record<string, unknown>;
    try {
      await api.post(`/api/v1/equipment/${equipment_id}/readings`, body);
      return { status: 'completed' };
    } catch (e) { return classifyAndThrow(e); }
  },

  // Field walk-around (Phase 7). Single POST; server dedupes replays on
  // (equipment_id, offline_id) → a replayed 200 is the same success as 201.
  'equipment.inspection': async (entry) => {
    const { equipment_id, ...body } = entry.payload as { equipment_id: string } & Record<string, unknown>;
    try {
      await api.post(`/api/v1/equipment/${equipment_id}/inspections`, body);
      return { status: 'completed' };
    } catch (e) { return classifyAndThrow(e); }
  },

  // Quick incident report (Phase 9) and the resumable with-photos variant —
  // replayCore (404 stays retryable for the plain report: contract-first).
  'incident.report': viaCore('incident.report'),
  'incident.report.full': viaCore('incident.report.full'),

  // Replace-set save of a daily log's usage segments (attachments rework
  // Phase 3). The PUT is naturally idempotent — the server replaces the log's
  // non-approved segments wholesale, and per-segment offline_ids dedupe — so
  // identical replays are safe. 409 domain conflicts (incompatible attachment,
  // time overlap, attachment busy on another machine, out-of-service asset)
  // are permanent for this payload — terminal, carrying the server's message
  // so the Sync Center shows WHY it needs attention.
  'equipmentUsage.replace': async (entry) => {
    const { dailyLogId, segments } = entry.payload as { dailyLogId: string; segments: unknown[] };
    try {
      await api.put(`/api/v1/daily-logs/${dailyLogId}/usage-segments`, { segments });
      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 409) {
        // The api client unwraps {error:{message}} envelopes into a plain
        // Error whose .message IS the server message (client.ts interceptor);
        // apiErrorMessage covers the raw-axios path.
        throw new TerminalError(
          apiErrorMessage(e) ?? (e instanceof Error ? e.message : 'Equipment usage conflict'),
        );
      }
      return classifyAndThrow(e);
    }
  },

  // Daily-log upsert and the resumable with-photos variant — replayCore.
  'dailyLog.upsert': viaCore('dailyLog.upsert'),
  'dailyLog.full': viaCore('dailyLog.full'),

  // Resumable inspection-with-photos: upsert (idempotent on
  // (equipment_id, offline_id)) → photo uploads (deduped on
  // (inspection_id, offline_id) server-side). Progress persists after each step
  // so a mid-flight failure resumes instead of duplicating. Mirrors
  // 'dailyLog.full'.
  'equipment.inspection.full': async (entry) => {
    const p = entry.payload as OfflineInspectionFull;
    const prog: InspectionFullProgress = (entry.progress as InspectionFullProgress) ?? {};
    const save = () => db.mutations.update(entry.id!, { progress: { ...prog } });

    try {
      if (!prog.inspectionId) {
        const created = await api.post<ApiResponse<{ id: string }>>(
          `/api/v1/equipment/${p.equipment_id}/inspections`,
          p.inspection,
        );
        prog.inspectionId = created.data.id;
        await save();
      }
      const iid = prog.inspectionId;

      for (let i = prog.attachmentsDone ?? 0; i < p.attachments.length; i++) {
        const a = p.attachments[i];
        const blob = (await getAttachment(a.attachmentId))?.blob;
        if (!blob) throw new TerminalError(`missing attachment blob ${a.attachmentId}`);
        // Keep in sync with features/equipment/photos.ts uploadInspectionPhoto (same multipart contract).
        const fd = new FormData();
        fd.append('file', blob, a.filename);
        fd.append('field_name', a.field_name);
        fd.append('offline_id', a.attachmentId);
        await api.post(`/api/v1/equipment/${p.equipment_id}/inspections/${iid}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        prog.attachmentsDone = i + 1;
        await save();
      }

      return { status: 'completed' };
    } catch (e) { return classifyAndThrow(e); }
  },

  // Resumable purchase-request DRAFT with photos/quotes (PO-FE-01): create
  // (idempotent on offline_id, contract §1) → attachment uploads. Progress
  // persists after each step so a mid-flight failure resumes instead of
  // duplicating. Mirrors 'incident.report.full', including its 404-stays-
  // retryable conductor rule (contract-first rollout: a draft queued before
  // PO-BE-01 deploys must land after it does).
  // Keep in sync with features/safety/photos.ts (same multipart contract).
  // Mirrors 'incident.report.full' including resumable progress.
  'safety.observation.full': async (entry) => {
    const p = entry.payload as OfflineSafetyObservationFull;
    const prog: SafetyObservationFullProgress = (entry.progress as SafetyObservationFullProgress) ?? {};
    const save = () => db.mutations.update(entry.id!, { progress: { ...prog } });

    try {
      if (!prog.observationId) {
        const created = await api.post<ApiResponse<{ id: string }>>('/api/v1/safety/observations', p.observation);
        prog.observationId = created.data.id;
        await save();
      }
      const oid = prog.observationId;

      for (let i = prog.attachmentsDone ?? 0; i < p.attachments.length; i++) {
        const a = p.attachments[i];
        const blob = (await getAttachment(a.attachmentId))?.blob;
        if (!blob) throw new TerminalError(`missing attachment blob ${a.attachmentId}`);
        const fd = new FormData();
        fd.append('file', blob, a.filename);
        fd.append('field_name', a.field_name);
        fd.append('offline_id', a.attachmentId);
        await api.post(`/api/v1/safety/observations/${oid}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        prog.attachmentsDone = i + 1;
        await save();
      }

      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 404) throw e; // contract-first: pre-deploy drafts stay retryable
      return classifyAndThrow(e);
    }
  },
  // Phase 3b: whole checklist in one idempotent POST (offline_id dedupes).
  'safety.inspection.submit': async (entry) => {
    try {
      await api.post('/api/v1/safety/inspections', entry.payload);
      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 404) throw e; // contract-first
      return classifyAndThrow(e);
    }
  },
  'safety.stopWork.create': async (entry) => {
    const p = entry.payload as OfflineStopWorkCreate;
    try {
      await api.post('/api/v1/safety/stop-work', p.event);
      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 404) throw e; // contract-first
      return classifyAndThrow(e);
    }
  },
  'purchaseRequest.draft': async (entry) => {
    const p = entry.payload as OfflinePurchaseRequestDraft;
    const prog: PurchaseRequestDraftProgress = (entry.progress as PurchaseRequestDraftProgress) ?? {};
    const save = () => db.mutations.update(entry.id!, { progress: { ...prog } });

    try {
      if (!prog.requestId) {
        const created = await api.post<ApiResponse<{ id: string }>>('/api/v1/purchase-requests', p.request);
        prog.requestId = created.data.id;
        await save();
      }
      const rid = prog.requestId;

      for (let i = prog.attachmentsDone ?? 0; i < p.attachments.length; i++) {
        const a = p.attachments[i];
        const blob = (await getAttachment(a.attachmentId))?.blob;
        if (!blob) throw new TerminalError(`missing attachment blob ${a.attachmentId}`);
        // Frozen contract §2 upload shape: file + document_type (invoice envelope).
        const fd = new FormData();
        fd.append('file', blob, a.filename);
        fd.append('document_type', a.document_type);
        await api.post(`/api/v1/purchase-requests/${rid}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        prog.attachmentsDone = i + 1;
        await save();
      }

      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 404) throw e; // pre-deploy: retryable, never terminal
      return classifyAndThrow(e);
    }
  },

  // Offline edit of an EXISTING draft (contract §1 allows draft update in the
  // outbox). Lines replace wholesale; the body carries the version loaded when
  // the edit was made — a 409 STALE_VERSION while queued is a real conflict
  // (someone changed it server-side), terminal with the server's message so
  // the Sync Center shows why.
  'purchaseRequest.draftUpdate': async (entry) => {
    const { request_id, body } = entry.payload as { request_id: string; body: Record<string, unknown> };
    try {
      await api.put(`/api/v1/purchase-requests/${request_id}`, body);
      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 404) throw e; // pre-deploy: retryable, never terminal
      if (httpStatus(e) === 409) {
        throw new TerminalError(
          apiErrorMessage(e) ?? (e instanceof Error ? e.message : 'This request was changed by someone else.'),
        );
      }
      return classifyAndThrow(e);
    }
  },

  // ScrapTrack offline ticket capture (parity Phase 9). Single POST; the server
  // is idempotent on `local_uuid` (a replay returns the existing ticket with
  // 200), so re-sends are always safe. A 409 duplicate NADC ticket number is
  // permanent — terminal, carrying the server's message so the offline queue
  // can show WHY it needs attention (original §4.13). Payload shape is
  // OfflineScrapTicket (features/scraptrack/offline/scrapOutbox.ts): the POST
  // body lives under `ticket`; `display` is queue-view-only and never sent.
  'scrapTicket.create': async (entry) => {
    const { ticket } = entry.payload as { ticket: Record<string, unknown> };
    try {
      await api.post('/api/v1/scraptrack/tickets/truck', ticket);
      return { status: 'completed' };
    } catch (e) {
      if (httpStatus(e) === 409) {
        throw new TerminalError(apiErrorMessage(e) ?? 'duplicate NADC ticket number');
      }
      return classifyAndThrow(e);
    }
  },

  // Resumable form submission (create → signatures → attachments → submit) —
  // replayCore.
  'formSubmission.full': viaCore('formSubmission.full'),
};
